import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import styles from "./Navbar.module.css";
import { useLocation } from "react-router-dom";
import EnrollmentForm from "../../pages/enrollment/EnrollmentForm";

const menuItems = [
  { title: "Home", link: "/" },
  {
    title: "Courses",
    type: "dropdown",
    items: [
      { name: "Fine Arts", link: "fine-arts" },
      { name: "Dance", link: "/dance" },
      { name: "Music", link: "/music" },
    ],
  },
  { title: "Faculty", link: "/faculty" },
  {
    title: "Programs",
    type: "dropdown",
    items: [
      { name: "Spring camp 2026", link: "/spring-camp" },
      { name: "Summer Camp 2026", link: "/summer-camp" },
    ],
  },
  {
    title: "Showcase",
    type: "dropdown",
    items: [
      { name: "Student artwork", link: "studentArt" },
      { name: "Faculty Artwork", link: "facultyArt" },
      { name: "Dance Gallery", link: "/dance-gallery" },
    ],
  },
  { title: "About", link: "about" }, // This will now show in both!
];

const Navbar = () => {
  const location = useLocation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [showNavbar, setShowNavbar] = useState(true);
  const lastScrollY = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 0) {
        setShowNavbar(true);
      } else if (currentScrollY < lastScrollY.current) {
        setShowNavbar(true);
      } else {
        setShowNavbar(false);
      }
      lastScrollY.current = currentScrollY;
    };

    lastScrollY.current = window.scrollY;
    setShowNavbar(window.scrollY <= 0);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Toggle accordion section
  const toggleAccordion = (title: string) => {
    setExpandedMenu(expandedMenu === title ? null : title);
  };

  const isCoursePage = location.pathname.includes("/course/");
  const isFacultyPage = location.pathname === "/faculty";
  const isHome = location.pathname === "/";

  // Helper to close drawer and open modal
  const handleMobileEnroll = () => {
    setIsMobileOpen(false);
    setModalOpen(true);
  };

  return (
    <nav
      className={`
      ${styles.navbar} 
      ${isCoursePage ? styles.navbarSolid : styles.navbarTransparent}
      ${isHome ? styles.isHomePage : ""} 
      ${isFacultyPage ? styles.floating : ""}
      ${showNavbar ? "" : styles.navbarHidden}
    `}
    >
    <div className={styles.logo}>
      <span>NUPUR CREATIONS</span>
    </div>

      {/* --- DESKTOP MENU --- */}
      <ul className={styles.desktopMenu}>
        {menuItems.map((item) => (
          <li
            key={item.title}
            className={item.type === "dropdown" ? styles.hasDropdown : ""}
            onMouseEnter={() =>
              item.type === "dropdown" && setActiveMenu(item.title)
            }
            onMouseLeave={() => setActiveMenu(null)}
          >
            {item.type === "dropdown" ? (
              <>
                <button className={styles.dropTrigger}>
                  {item.title} <ChevronDown size={14} />
                </button>
                <AnimatePresence>
                  {activeMenu === item.title && (
                    <motion.div
                      className={styles.dropdown}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                    >
                      {item.items?.map((sub) => (
                        <a key={sub.name} href={sub.link}>
                          {sub.name}
                        </a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <a href={item.link}>{item.title}</a>
            )}
          </li>
        ))}
        <li>
          <button
            className={styles.enrollBtn}
            onClick={() => setModalOpen(true)}
          >
            Enroll Now
          </button>
        </li>
      </ul>

      {/* --- MOBILE TRIGGER (Hamburger) --- */}
      <button
        className={styles.mobileToggle}
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle Menu"
      >
        {isMobileOpen ? <X size={28} /> : <Menu size={28} />}
      </button>

      {/* --- MOBILE DRAWER & BACKDROP --- */}
      {/* --- MOBILE DRAWER --- */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              className={styles.backdrop}
              onClick={() => setIsMobileOpen(false)}
            />
            <motion.div
              className={styles.mobileDrawer} /* ... animation props ... */
            >
              <div className={styles.mobileContent}>
                {menuItems.map((item) => (
                  <div key={item.title} className={styles.mobileItemBlock}>
                    {item.type === "dropdown" ? (
                      <>
                        {/* Accordion Trigger */}
                        <button
                          className={styles.mobileAccordionTrigger}
                          onClick={() => toggleAccordion(item.title)}
                        >
                          {item.title}
                          <motion.span
                            animate={{
                              rotate: expandedMenu === item.title ? 180 : 0,
                            }}
                          >
                            <ChevronDown size={18} />
                          </motion.span>
                        </button>

                        {/* Accordion Content */}
                        <AnimatePresence>
                          {expandedMenu === item.title && (
                            <motion.div
                              className={styles.mobileSubGroup}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                              {item.items?.map((sub) => (
                                <a
                                  key={sub.name}
                                  href={sub.link}
                                  onClick={() => setIsMobileOpen(false)}
                                >
                                  {sub.name}
                                </a>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <a
                        href={item.link}
                        className={styles.mobileLink}
                        onClick={() => setIsMobileOpen(false)}
                      >
                        {item.title}
                      </a>
                    )}
                  </div>
                ))}

                <button
                  className={styles.mobileEnrollBtn}
                  onClick={handleMobileEnroll}
                >
                  Enroll Now
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Enrollment Form Component */}
      {isModalOpen && (
        <EnrollmentForm
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          selectedProgram=""
        />
      )}
    </nav>
  );
};

export default Navbar;
